import { useState, useEffect } from 'react'
import { Clock, ThumbsUp, MapPin, CreditCard, XCircle } from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import styles from './Intersetorial.module.css'

interface DDDData {
  ddd: string
  regiao: string
  quantidade: number
  percentual: number
}

export function Intersetorial() {
  const { queryParams, filtersVersion } = useFilters()
  const [dddData, setDddData] = useState<DDDData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [kpis, setKpis] = useState({
    tempoMedioRecebimento: 0,
    aprovacaoCredito: 0,
    taxaCancelamento: 0,
    clientesAprovados: 0,
    clientesReprovados: 0,
    cancelamentos: 0,
    totalVendas: 0,
    totalReservas: 0,
  })

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      setError(null)
      try {
        // Carrega dados de DDD e KPIs intersetoriais em paralelo
        const [dddResponse, intersetorialResponse] = await Promise.all([
          api.getLeadsPorDDD(queryParams, 10),
          api.getIntersetorialKPIs(queryParams).catch(() => null)
        ])

        // Processa dados de DDD
        const total = dddResponse.reduce((acc: number, item: any) => acc + (item.quantidade || 0), 0)
        const formattedData: DDDData[] = dddResponse.map((item: any) => ({
          ddd: item.ddd || 'N/A',
          regiao: getRegiaoByDDD(item.ddd),
          quantidade: item.quantidade || 0,
          percentual: total > 0 ? ((item.quantidade || 0) / total) * 100 : 0,
        }))
        setDddData(formattedData)

        // Processa KPIs intersetoriais
        if (intersetorialResponse) {
          setKpis({
            tempoMedioRecebimento: intersetorialResponse.kpis?.tempo_medio_recebimento || 0,
            aprovacaoCredito: intersetorialResponse.kpis?.aprovacao_credito || 0,
            taxaCancelamento: intersetorialResponse.kpis?.taxa_cancelamento || 0,
            clientesAprovados: intersetorialResponse.contagens?.clientes_aprovados || 0,
            clientesReprovados: intersetorialResponse.contagens?.clientes_reprovados || 0,
            cancelamentos: intersetorialResponse.contagens?.cancelamentos || 0,
            totalVendas: intersetorialResponse.contagens?.total_vendas || 0,
            totalReservas: intersetorialResponse.contagens?.total_reservas || 0,
          })
        }
      } catch (err) {
        setError('Erro ao carregar dados intersetoriais')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [filtersVersion, queryParams])

  function getRegiaoByDDD(ddd: string): string {
    const regioes: Record<string, string> = {
      '11': 'Sao Paulo - Capital',
      '12': 'Sao Paulo - Vale do Paraiba',
      '13': 'Sao Paulo - Baixada Santista',
      '14': 'Sao Paulo - Bauru',
      '15': 'Sao Paulo - Sorocaba',
      '16': 'Sao Paulo - Ribeirao Preto',
      '17': 'Sao Paulo - Sao Jose do Rio Preto',
      '18': 'Sao Paulo - Presidente Prudente',
      '19': 'Sao Paulo - Campinas',
      '21': 'Rio de Janeiro - Capital',
      '22': 'Rio de Janeiro - Campos',
      '24': 'Rio de Janeiro - Petropolis',
      '27': 'Espirito Santo',
      '28': 'Espirito Santo - Sul',
      '31': 'Belo Horizonte',
      '32': 'Minas Gerais - Juiz de Fora',
      '33': 'Minas Gerais - Governador Valadares',
      '34': 'Minas Gerais - Uberlandia',
      '35': 'Minas Gerais - Pocos de Caldas',
      '37': 'Minas Gerais - Divinopolis',
      '38': 'Minas Gerais - Montes Claros',
      '41': 'Curitiba',
      '42': 'Parana - Ponta Grossa',
      '43': 'Parana - Londrina',
      '44': 'Parana - Maringa',
      '45': 'Parana - Cascavel',
      '46': 'Parana - Francisco Beltrao',
      '47': 'Santa Catarina - Joinville',
      '48': 'Santa Catarina - Florianopolis',
      '49': 'Santa Catarina - Chapeco',
      '51': 'Porto Alegre',
      '53': 'Rio Grande do Sul - Pelotas',
      '54': 'Rio Grande do Sul - Caxias do Sul',
      '55': 'Rio Grande do Sul - Santa Maria',
      '61': 'Brasilia',
      '62': 'Goiania',
      '63': 'Tocantins',
      '64': 'Goias - Rio Verde',
      '65': 'Cuiaba',
      '66': 'Mato Grosso - Rondonopolis',
      '67': 'Campo Grande',
      '68': 'Rio Branco',
      '69': 'Porto Velho',
      '71': 'Salvador',
      '73': 'Bahia - Itabuna',
      '74': 'Bahia - Juazeiro',
      '75': 'Bahia - Feira de Santana',
      '77': 'Bahia - Barreiras',
      '79': 'Sergipe',
      '81': 'Recife',
      '82': 'Maceio',
      '83': 'Joao Pessoa',
      '84': 'Natal',
      '85': 'Fortaleza',
      '86': 'Teresina',
      '87': 'Pernambuco - Petrolina',
      '88': 'Ceara - Juazeiro do Norte',
      '89': 'Piaui - Interior',
      '91': 'Belem',
      '92': 'Manaus',
      '93': 'Para - Santarem',
      '94': 'Para - Maraba',
      '95': 'Boa Vista',
      '96': 'Macapa',
      '97': 'Amazonas - Interior',
      '98': 'Sao Luis',
      '99': 'Maranhao - Imperatriz',
    }
    return regioes[ddd] || 'Outras Regioes'
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando dados intersetoriais...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>KPIs Intersetoriais</h1>
            <p className={styles.pageSubtitle}>Integracao Comercial + Marketing + Financeiro</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>KPIs Intersetoriais</h1>
          <p className={styles.pageSubtitle}>Integracao Comercial + Marketing + Financeiro</p>
        </div>
      </div>

      {/* Integracao com Marketing */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Integracao com Marketing</h2>
        <div className={styles.kpiGrid3}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Clock size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Tempo Medio Resposta ao Lead</span>
              <span className={styles.kpiValue}>{kpis.tempoMedioRecebimento.toFixed(1)} min</span>
              <span className={`${styles.kpiMeta} ${kpis.tempoMedioRecebimento <= 60 ? styles.success : styles.warning}`}>
                {kpis.tempoMedioRecebimento <= 60 ? 'Dentro do esperado' : 'Acima do esperado'}
              </span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.secondary}`}>
              <ThumbsUp size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Total de Vendas no Periodo</span>
              <span className={styles.kpiValue}>{kpis.totalVendas}</span>
              <span className={styles.kpiMeta}>Vendas efetivadas</span>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <MapPin size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Taxa de Leads por DDD</span>
              <span className={styles.kpiValue}>Top 3</span>
              <span className={styles.kpiMeta}>
                {dddData.slice(0, 3).map((d) => `${d.ddd} (${d.percentual.toFixed(0)}%)`).join(' - ')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Integracao com Financeiro */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Integracao com Financeiro</h2>
        <div className={styles.kpiGrid2}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <CreditCard size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Aprovacao de Credito/Financiamento</span>
              <span className={styles.kpiValue}>{kpis.aprovacaoCredito.toFixed(1)}%</span>
              <span className={styles.kpiMeta}>% de clientes com credito aprovado</span>
              <div className={styles.kpiBreakdown}>
                <span className={`${styles.breakdownItem} ${styles.success}`}>
                  Aprovados: {kpis.clientesAprovados}
                </span>
                <span className={`${styles.breakdownItem} ${styles.error}`}>
                  Reprovados: {kpis.clientesReprovados}
                </span>
              </div>
            </div>
          </div>

          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.error}`}>
              <XCircle size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Taxa de Cancelamento/Distrato</span>
              <span className={styles.kpiValue}>{kpis.taxaCancelamento.toFixed(1)}%</span>
              <span className={`${styles.kpiMeta} ${kpis.taxaCancelamento < 5 ? styles.success : styles.warning}`}>
                Meta: abaixo de 5%
              </span>
              <div className={styles.kpiBreakdown}>
                <span className={styles.breakdownItem}>Cancelamentos: {kpis.cancelamentos}</span>
                <span className={styles.breakdownItem}>Total reservas: {kpis.totalReservas}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Distribuicao de Leads por DDD */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Distribuicao de Leads por DDD</h2>
        <div className={styles.dddGrid}>
          {dddData.slice(0, 8).map((item) => (
            <div key={item.ddd} className={styles.dddCard}>
              <span className={styles.dddCode}>{item.ddd}</span>
              <span className={styles.dddRegion}>{item.regiao}</span>
              <span className={styles.dddValue}>{item.quantidade.toLocaleString('pt-BR')} leads</span>
              <div className={styles.dddBar}>
                <div
                  className={styles.dddBarFill}
                  style={{ width: `${Math.min(100, item.percentual)}%` }}
                />
              </div>
              <span className={styles.dddPercent}>{item.percentual.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </section>

      {/* Tabela Detalhada */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Detalhamento por Regiao</h2>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>DDD</th>
                <th>Regiao</th>
                <th>Quantidade</th>
                <th>Percentual</th>
                <th>Distribuicao</th>
              </tr>
            </thead>
            <tbody>
              {dddData.map((item) => (
                <tr key={item.ddd}>
                  <td>
                    <span className={styles.dddBadge}>{item.ddd}</span>
                  </td>
                  <td>{item.regiao}</td>
                  <td>{item.quantidade.toLocaleString('pt-BR')}</td>
                  <td>{item.percentual.toFixed(1)}%</td>
                  <td>
                    <div className={styles.tableBar}>
                      <div
                        className={styles.tableBarFill}
                        style={{ width: `${Math.min(100, item.percentual)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}
